/**
 * @fileoverview Componente principal Organism para el sidebar derecho del Inbox.
 * Orquesta la visualización de datos del cliente, tabs, loading y componentes relacionados, con lógica optimizada y modular.
 * @module InboxRightSidebarMC
 * @category Component - Organism
 * @since 1.0.0
 * @version 2.1.0
 */
import React, { useMemo, useCallback, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { isWhatsAppMsPlatform } from 'services/inbox/inboxMsChatPlatform';
import IconMC from '../../../../components/IconMC';
import TabsComponentMC from '../../../../components/TabsComponentMC';
import { CustomerCardMC } from '../../../../modules/inbox/components';
import {
  CustomerDetailsMC,
  CustomerFunnelsMC,
  CustomerIntegrationsMC,
  CustomerOrdersMC,
  CustomerReminderMC
} from '../../../../modules/inbox';
import LoadingMC from '../../../../components/LoadingMC';
import ButtonComponent from '../../../../components/ButtonMC';
import { useTraceUpdate } from '../../../../hooks/shared/useDebugRenders';
import useInboxRightSidebarData from '../../../../hooks/inbox/data/useInboxRightSidebarData';
import useInboxRightSidebarUI from '../../../../hooks/inbox/ui/useInboxRightSidebarUI';
import useEnsureInboxCustomer from '../../../../hooks/inbox/actions/useEnsureInboxCustomer';
import Memory from '../../../../modules/Automations/ChatbotIA/Config/Memory';
import COMMENT_THREAD_PLATFORMS from '../../../../hooks/inbox/constants/commentThreadPlatforms';

/** Tabs del sidebar que requieren un cliente CRM vinculado al chat (customer_id en la conversación). */
const CRM_SIDEBAR_TAB_IDS = new Set(['customer-info', 'order-info', 'funnel-info']);

/**
 * @typedef {Object} InboxRightSidebarMCProps
 * @description Este componente no recibe props directos, utiliza datos de hooks y Redux.
 */

/**
 * Componente principal Organism para el sidebar derecho del Inbox.
 *
 * Atomic Design: Organism
 * - Orquesta CustomerCardMC, CustomerDetailsMC, CustomerFunnelsMC, CustomerIntegrationsMC, CustomerOrdersMC, CustomerReminderMC, TabsComponentMC, LoadingMC
 * - Permite alternar entre tabs, mostrar loading y datos del cliente
 * - Utiliza TailwindCSS con .tw-wrapper
 * - Optimizado con memo, useMemo y useCallback
 *
 * @component InboxRightSidebarMC
 * @category Component - Organism
 * @param {InboxRightSidebarMCProps} props - No recibe props directos
 * @returns {JSX.Element} Elemento JSX renderizado
 * @example
 * <InboxRightSidebarMC />
 * @see CustomerCardMC
 * @see CustomerDetailsMC
 * @see CustomerFunnelsMC
 * @see CustomerIntegrationsMC
 * @see CustomerOrdersMC
 * @see CustomerReminderMC
 * @see TabsComponentMC
 * @see LoadingMC
 * @since 1.0.0
 * @version 2.1.0 - Optimización de diseño y documentación
 */

// Componente principal del sidebar derecho en Inbox
const InboxRightSidebarMC = React.memo(({ className = '' }) => {
  const { t } = useTranslation('InboxMC');

  // Data Layer: carga de datos puros
  const {
    conversation,
    customerData,
    orderCounts,
    loading,
    refreshCustomerData,
    leadScore,
    leadScoreLoading
  } = useInboxRightSidebarData();

  // UI Layer: estado de interfaz puro
  const {
    activeTab,
    handleTabChange: handleTabChangeUI
  } = useInboxRightSidebarUI('customer-info');

  const { ensureInboxCustomer: ensureCrmCustomer, isEnsuring: isEnsuringCustomer } = useEnsureInboxCustomer(conversation);

  /**
   * Al abrir pestañas CRM, si el chat aún no tiene customer_id se crea el cliente (API) y se cargan datos;
   * si ya existe customer_id, el comportamiento es el mismo que antes.
   */
  const handleSidebarTabChange = useCallback(async (tabId) => {
    const needsCustomerForTab = CRM_SIDEBAR_TAB_IDS.has(tabId);
    if (needsCustomerForTab && conversation?.id && !conversation.customerId) {
      const result = await ensureCrmCustomer();
      if (!result?.success) {
        return;
      }
    }
    handleTabChangeUI(tabId);
  }, [conversation?.id, conversation?.customerId, ensureCrmCustomer, handleTabChangeUI]);

  const displayCardData = useMemo(() => {
    if (customerData) return customerData;
    if (!conversation?.id) return null;
    return {
      fullName: conversation.fullName || t('sidebar.chat_contact'),
      formattedPhone: conversation.phone || '',
      email: conversation.email || '',
      hasEmail: Boolean(String(conversation.email || '').trim()),
      hasPhone: Boolean(String(conversation.phone || '').trim()),
    };
  }, [customerData, conversation, t]);

  const crmRequiredPlaceholder = useMemo(() => (
    <div className="tw-py-6 tw-px-2 tw-text-center tw-text-sm tw-text-gray-600">
      {t('sidebar.crm_tab_requires_customer')}
    </div>
  ), [t]);

  const crmTabLoadingContent = useMemo(() => (
    <div className="tw-py-6 tw-flex tw-justify-center">
      <LoadingMC loading />
    </div>
  ), []);

  // Memoización estricta de contenidos de tabs
  const customerDetailsContent = useMemo(() => {
    if (!conversation?.id) return null;
    if (loading && !customerData) return crmTabLoadingContent;
    if (!customerData) return crmRequiredPlaceholder;
    return (
      <CustomerDetailsMC
        key={`customer-details-${conversation.id}`}
        conversation={conversation}
        customerData={customerData}
        refreshCustomerData={refreshCustomerData}
      />
    );
  }, [conversation, customerData, crmRequiredPlaceholder, crmTabLoadingContent, loading, refreshCustomerData]);

  // Memoización estricta de contenidos de tabs
  const customerOrdersContent = useMemo(() => {
    if (!conversation?.id) return null;
    if (loading && !customerData) return crmTabLoadingContent;
    if (!customerData) return crmRequiredPlaceholder;
    return (
      <CustomerOrdersMC
        key={`customer-orders-${conversation.id}`}
        conversation={conversation}
        customerData={customerData}
      />
    );
  }, [conversation, customerData, crmRequiredPlaceholder, crmTabLoadingContent, loading]);

  // Memoización estricta de contenidos de tabs
  const customerFunnelsContent = useMemo(() => {
    if (!conversation?.id) return null;
    if (!customerData && !conversation.customerId) {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-6 tw-text-center tw-px-2">
          <p className="tw-text-sm tw-text-gray-700 tw-mb-3">{t('Funnels.ensure_customer_prompt')}</p>
          <ButtonComponent
            outlined
            text={t('Funnels.ensure_customer_cta')}
            onClick={() => ensureCrmCustomer()}
            disabled={isEnsuringCustomer}
            className="!tw-bg-white !tw-text-blue-500 tw-text-default tw-font-medium"
          />
        </div>
      );
    }
    const funnelCustomerData = customerData || { id: conversation.customerId };
    return (
      <CustomerFunnelsMC
        key={`customer-funnels-${conversation.id}`}
        customerData={funnelCustomerData}
      />
    );
  }, [conversation, customerData, ensureCrmCustomer, isEnsuringCustomer, t]);

  // Memoización estricta de contenidos de tabs
  const customerReminderContent = useMemo(() => {
    if (!conversation?.id || !customerData) return null;
    return (
      <CustomerReminderMC
        key={`customer-reminder-${conversation.id}`}
        customerData={customerData}
      />
    );
  }, [conversation?.id, customerData]);

  // Memoización estricta de contenidos de tabs
  const customerIntegrationsContent = useMemo(() => {
    if (!conversation?.id) return null;
    if (loading && !customerData) return crmTabLoadingContent;
    if (!customerData) return crmRequiredPlaceholder;
    return (
      <CustomerIntegrationsMC
        key={`customer-integrations-${conversation.id}`}
        customerData={customerData}
        fetchCustomerData={refreshCustomerData}
      />
    );
  }, [conversation?.id, customerData, refreshCustomerData, crmRequiredPlaceholder, crmTabLoadingContent, loading]);

  const memoryCustomerId = customerData?.id ?? conversation?.customerId;
  const sidebarCustomerId = memoryCustomerId != null ? String(memoryCustomerId) : memoryCustomerId;

  const memoryContent = useMemo(
    () => <Memory newInbox customerId={sidebarCustomerId} />,
    [sidebarCustomerId]
  );

  // Negociaciones y memoria de MIA cuelgan del customer, no del canal: aplican a cualquier
  // DM y solo se excluyen los hilos de comentarios, donde ni se negocia ni corre MIA.
  //
  // Antes esto era una lista blanca de `PLATFORM_CONFIG.{whatsapp,messenger,instagram}
  // .platformKey`, o sea las claves cortas del monolito. El microservicio nunca emite esas
  // —manda tik_tok_messages, whatsapp_bsp_messages, facebook_messages…—, así que ambas
  // pestañas desaparecían en todo chat servido por el MS, no solo en TikTok.
  const isCommentThread = useMemo(
    () => !conversation?.platform || COMMENT_THREAD_PLATFORMS.has(conversation.platform),
    [conversation?.platform]
  );

  // Memoización de tabsConfiguration
  const tabsConfiguration = useMemo(() => ([
    {
      id: 'customer-info',
      label: <IconMC name="people-outline" size="base" />,
      content: customerDetailsContent,
      disabled: false
    },
    {
      id: 'order-info',
      label: <IconMC name="shopping-bag-outline" size="base" />,
      content: customerOrdersContent,
      disabled: false
    },
    {
      id: 'funnel-info',
      label: <IconMC name="funnel-outline" size="base" />,
      content: customerFunnelsContent,
      disabled: isCommentThread
    },
    {
      id: 'memory-info',
      label: <IconMC name="memory-outline" size="base" />,
      content: memoryContent,
      disabled: isCommentThread
    },
    {
      id: 'flow-info',
      label: <IconMC name="mia-bot" size="base" />,
      content: customerReminderContent,
      disabled: !isWhatsAppMsPlatform(conversation?.platform)
    },
    {
      id: 'hubspot-info',
      label: <IconMC name="stats" size="base" />,
      content: customerIntegrationsContent,
      disabled: false
    }
  ]), [
    customerDetailsContent,
    customerOrdersContent,
    customerFunnelsContent,
    customerReminderContent,
    customerIntegrationsContent,
    memoryContent,
    conversation?.platform,
    isCommentThread
  ]);

  // Debug Render
  useTraceUpdate('InboxRightSidebarMC', {
    platform: conversation?.platform,
    conversationId: conversation?.id,
    orderCounts,
    customerData,
    loading,
    activeTab,
    hasConversationId: !!conversation?.id,
    hasCustomerData: !!customerData,
    hasCustomer: !!customerData?.customer,
    hasSerializedConversation: !!customerData?.serializedConversation,
    ordersCount: customerData?.customer?.orders_count || 0
  }, {
    logRenderCount: true,
    logPropChanges: true,
    logStackTrace: false
  });

  const enabledTabsConfiguration = useMemo(() => tabsConfiguration.filter((tab) => !tab.disabled), [tabsConfiguration]);
  const gridColumnsClassName = useMemo(() => {
    const gridColumnsMap = {
      1: 'tw-grid-cols-1',
      2: 'tw-grid-cols-2',
      3: 'tw-grid-cols-3',
      4: 'tw-grid-cols-4',
      5: 'tw-grid-cols-5',
      6: 'tw-grid-cols-6'
    };

    return gridColumnsMap[enabledTabsConfiguration.length] || 'tw-grid-cols-1';
  }, [enabledTabsConfiguration.length]);

  // Empty state
  if (!conversation?.id) {
    return (
      <div className={`tw-flex tw-items-center tw-justify-center tw-h-full tw-bg-white tw-border-l tw-border-gray-200 tw-w-72 ${className}`}>
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
          <IconMC name="person-outline" size="5xl" className="tw-text-blue-500" />
          <p className="tw-text-sm tw-font-medium tw-text-gray-800">{t('sidebar.no_conversation')}</p>
        </div>
      </div>
    );
  }

  if (!displayCardData) return null;

  return (
    <Suspense fallback={<div className="loading" />}>
      <div className={`tw-flex tw-flex-col tw-h-full tw-bg-white tw-border-l tw-border-gray-200 tw-w-72 ${className}`}>
        <CustomerCardMC
          customerData={displayCardData}
          orderCounts={orderCounts}
        />
        <div className="tw-flex-1 tw-overflow-hidden tw-mt-1">
          <TabsComponentMC
            tabsWrapperClassName="tw-m-0"
            contentClassName="tw-px-4 tw-py-4"
            tabsContainerClassName={`tw-w-full tw-grid ${gridColumnsClassName} tw-gap-0 tw-px-0`}
            activeTabClassName="tw-w-full tw-justify-center tw-text-center tw-py-3 tw-bg-blue-50 tw-text-blue-500 !tw-rounded-none tw-cursor-pointer"
            inactiveTabClassName="tw-w-full tw-justify-center tw-text-center tw-py-3 !tw-border-b !tw-border-gray-200 tw-cursor-pointer !tw-rounded-none hover:tw-bg-gray-50"
            tabList={enabledTabsConfiguration}
            activeTab={activeTab}
            onTabChange={handleSidebarTabChange}
          />
        </div>
      </div>
    </Suspense>
  );
});

export default InboxRightSidebarMC;
